/**
 * FunnelPerformancePage — Measure pillar analytics surface.
 *
 * Per audit deliverable 06B. Revenue-attribution view for nurture / conversion
 * funnels (a.k.a. "flows"). Read-only — deep-links back to the Conversion &
 * Nurture Engine (Free-Tier surface) which owns funnel authoring.
 *
 * Data sources:
 *   - GET /api/admin/email-analytics/revenue/flows?period=<today|Nd|custom&from&to>
 *       → { flows: [{ flow_id, flow_name, status, revenue_cents, attributed_events }] }
 *   - GET /api/admin/email-funnels/overview  (optional enrichment)
 *       → { summary: {...}, flows: [...] }  — used for active-enrollment context
 *         if present; failures here never block the page.
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Divider,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PillarPageShell from "../shared/PillarPageShell";
import TimeRangeFilter from "../../common/TimeRangeFilter";
import useTimeRangeParam from "../../common/useTimeRangeParam";
import { emailAnalyticsQuery } from "../shared/emailAnalyticsRange";
import { describeRange } from "../../common/timeRange";
import apiService from "../../../services/api";

// ─────────────────────────────────────────────────────────────────────────
// Types — mirror the campaigns revenue shape.
// ─────────────────────────────────────────────────────────────────────────
interface FlowRevenueRow {
  flow_id: string;
  flow_name: string;
  status: string | null;
  revenue_cents: number | null;
  attributed_events: number | null;
}

interface FlowsResponse {
  flows?: FlowRevenueRow[];
}

interface FunnelOverviewResponse {
  summary?: Record<string, number>;
  flows?: Array<{
    id?: string;
    active_enrollments?: number;
    total_enrolled?: number;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────
const formatUsd = (cents: number | null | undefined): string => {
  if (cents == null || Number.isNaN(cents)) return "—";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatInt = (n: number | null | undefined): string =>
  n == null || Number.isNaN(n) ? "—" : n.toLocaleString("en-US");

type ChipColor =
  | "default"
  | "success"
  | "warning"
  | "error"
  | "info";

const statusChip = (status: string | null | undefined) => {
  const raw = (status ?? "").toLowerCase();
  let color: ChipColor = "default";
  if (raw === "active" || raw === "live" || raw === "running") color = "success";
  else if (raw === "paused" || raw === "draft" || raw === "scheduled")
    color = "warning";
  else if (raw === "archived" || raw === "disabled" || raw === "stopped")
    color = "default";
  else if (raw === "error" || raw === "failed") color = "error";
  else if (raw) color = "info";

  const label = status
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : "Unknown";

  return (
    <Chip
      size="small"
      label={label}
      color={color}
      variant={color === "default" ? "outlined" : "filled"}
      sx={{ fontWeight: 600, textTransform: "none" }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────
const FunnelPerformancePage: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useTimeRangeParam({ type: "30d" });
  const [flows, setFlows] = useState<FlowRevenueRow[] | null>(null);
  const [activeEnrollments, setActiveEnrollments] = useState<number | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await apiService.get<FlowsResponse | FlowRevenueRow[]>(
          `/api/admin/email-analytics/revenue/flows?${emailAnalyticsQuery(period)}`,
        );
        if (!mounted) return;

        // Backend may return { flows: [...] } or be unwrapped to the array.
        const payload = res?.data as any;
        const rows: FlowRevenueRow[] = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.flows)
            ? payload.flows
            : [];
        setFlows(rows);
      } catch (e: any) {
        if (mounted) {
          setError(e?.message ?? "Failed to load funnel revenue");
          // Leave `flows` NULL. setFlows([]) turned a failed request into a
          // confident "$0.00 / 0 events / 0 funnels" dashboard under a soft
          // warning banner. Null flows render '—' everywhere, matching
          // CampaignPerformancePage's discipline.
          setFlows(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    // Optional enrichment — active enrollments for context. Never blocks.
    (async () => {
      try {
        const res = await apiService.get<FunnelOverviewResponse>(
          "/api/admin/email-funnels/overview",
        );
        if (!mounted) return;
        const payload = res?.data as any;
        const summary = payload?.summary ?? {};
        const overviewFlows: any[] = Array.isArray(payload?.flows)
          ? payload.flows
          : [];
        const summaryActive =
          summary.active_enrollments ??
          summary.total_active_enrollments ??
          null;
        const computed = overviewFlows.reduce(
          (acc, f) => acc + (Number(f?.active_enrollments) || 0),
          0,
        );
        const active =
          summaryActive != null
            ? Number(summaryActive)
            : overviewFlows.length
              ? computed
              : null;
        if (active != null && !Number.isNaN(active)) {
          setActiveEnrollments(active);
        }
      } catch {
        // Soft-fail: enrollment context is a nice-to-have only.
      }
    })();

    return () => {
      mounted = false;
    };
  }, [period]);

  // Derived KPIs
  const funnelCount = flows?.length ?? null;
  const totalRevenueCents =
    flows && flows.length
      ? flows.reduce((acc, f) => acc + (Number(f.revenue_cents) || 0), 0)
      : flows
        ? 0
        : null;
  const totalEvents =
    flows && flows.length
      ? flows.reduce((acc, f) => acc + (Number(f.attributed_events) || 0), 0)
      : flows
        ? 0
        : null;

  // Sort flows by revenue desc for the table (highest earners first).
  const sortedFlows = flows
    ? [...flows].sort(
        (a, b) => (Number(b.revenue_cents) || 0) - (Number(a.revenue_cents) || 0),
      )
    : [];

  return (
    <PillarPageShell
      pillar="measure"
      title="Funnel Performance"
      subtitle={`Per-funnel revenue attribution over ${describeRange(period)}. Read-only — funnel authoring lives in the Conversion & Nurture Engine.`}
      rightActions={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TimeRangeFilter value={period} onChange={setPeriod} />
        <Button
          variant="outlined"
          size="small"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate("/dashboard/communications/marketing/funnels")}
          sx={{
            borderColor: "#1A5F7A",
            color: "#1A5F7A",
            fontWeight: 700,
            "&:hover": { borderColor: "#15506A", bgcolor: "rgba(26,95,122,0.06)" },
          }}
        >
          Manage funnels
        </Button>
        </Box>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Couldn&apos;t load funnel revenue.</strong> {error} — the
          figures below are unknown, not zero.
        </Alert>
      )}

      {/* KPI cards */}
      {loading ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ borderRadius: 2, borderLeft: "6px solid #1A5F7A", height: "100%" }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  Funnels
                </Typography>
                <Typography variant="h4" sx={{ color: "#1A5F7A", fontWeight: 700, mt: 0.5 }}>
                  {funnelCount != null ? funnelCount.toLocaleString("en-US") : "—"}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Attributing revenue this period
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ borderRadius: 2, borderLeft: "6px solid #146152", height: "100%" }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  Total Revenue
                </Typography>
                <Typography variant="h4" sx={{ color: "#146152", fontWeight: 700, mt: 0.5 }}>
                  {formatUsd(totalRevenueCents)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Attributed · last 30 days
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ borderRadius: 2, borderLeft: "6px solid #D4AC0D", height: "100%" }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  Attributed Events
                </Typography>
                <Typography variant="h4" sx={{ color: "#B8860B", fontWeight: 700, mt: 0.5 }}>
                  {formatInt(totalEvents)}
                </Typography>
                {/* The caption used to describe ENROLMENTS under an
                    ATTRIBUTED-EVENTS number. The metric is conversions credited
                    to a funnel; enrolments are separate context, so they are
                    now labelled as such on their own line. */}
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                  Conversions credited to a funnel · last 30 days
                </Typography>
                {activeEnrollments != null && (
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                    {activeEnrollments.toLocaleString("en-US")} members currently enrolled
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Per-funnel table */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", fontWeight: 700 }}
          >
            Revenue by funnel
          </Typography>
        </CardContent>
        <Divider />

        {loading ? (
          <Box sx={{ p: 2 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} variant="text" height={40} />
            ))}
          </Box>
        ) : sortedFlows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {flows == null
                ? "Funnel revenue could not be loaded — this table is unknown, not empty."
                : "No funnel revenue attributed yet for this period."}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary" } }}>
                  <TableCell>Funnel</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Attributed events</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFlows.map((f) => {
                  const cents = Number(f.revenue_cents);
                  const hasRevenue = f.revenue_cents != null && !Number.isNaN(cents) && cents > 0;
                  return (
                    <TableRow key={f.flow_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {f.flow_name || "Untitled funnel"}
                        </Typography>
                      </TableCell>
                      <TableCell>{statusChip(f.status)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: hasRevenue ? "#146152" : "text.secondary",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatUsd(f.revenue_cents)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {formatInt(f.attributed_events)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </PillarPageShell>
  );
};

export default FunnelPerformancePage;
